import apiClient from "./axiosConfig";
import { InvoiceStatus } from "./billing";

// Public pay-page API (/api/billing/public). No login needed; rate limited per IP.

export interface PublicInvoiceItem {
  description: string;
  qty: number;
  unitPrice: number;
  amount: number;
}

/** Who is paid and how else to pay (SAP env); parts that aren't configured are null. */
export interface PublicPayee {
  name: string;
  address: string | null;
  gstin: string | null;
  supportEmail: string | null;
  bankTransfer: {
    accountName: string | null;
    accountNumber: string;
    ifsc: string;
    bankName: string | null;
  } | null;
  upiId: string | null;
  policies: { terms: string | null; privacy: string | null; refunds: string | null };
}

export interface PublicInvoice {
  invoiceNumber: string | null;
  tenantName: string | null;
  title: string | null;
  /** Paise still to pay (never negative). */
  amountDue: number;
  total: number;
  amountPaid: number;
  currency: string;
  dueDate: string;
  status: InvoiceStatus;
  paidAt: string | null;
  items: PublicInvoiceItem[];
  /** False when nothing is due, the invoice is paid / void, or online payment is unavailable. */
  payable: boolean;
  payee: PublicPayee;
}

export interface PublicStatementLine {
  invoiceNumber: string | null;
  title: string | null;
  dueDate: string;
  /** Paise still to pay on this invoice. */
  amountDue: number;
  overdue: boolean;
}

/** Everything an account owes now (/pay/s/:token), paid in one go. */
export interface PublicStatement {
  tenantName: string | null;
  currency: string;
  /** Paise: the sum of the lines; 0 when nothing is due. */
  amountDue: number;
  invoices: PublicStatementLine[];
  /** False when nothing is due or online payment is unavailable. */
  payable: boolean;
  payee: PublicPayee;
}

export const publicPayKeys = {
  invoice: (token: string) => ["public-pay", token] as const,
  statement: (token: string) => ["public-pay", "statement", token] as const,
};

export const fetchPublicStatement = async (token: string): Promise<PublicStatement> => {
  const res = await apiClient.get<PublicStatement>(
    `/api/billing/public/statements/${encodeURIComponent(token)}`
  );
  return res.data;
};

export const startStatementCheckout = async (token: string): Promise<{ url: string }> => {
  const res = await apiClient.post<{ url: string }>(
    `/api/billing/public/statements/${encodeURIComponent(token)}/checkout`
  );
  return res.data;
};

export const fetchPublicInvoice = async (token: string): Promise<PublicInvoice> => {
  const res = await apiClient.get<PublicInvoice>(
    `/api/billing/public/pay/${encodeURIComponent(token)}`
  );
  return res.data;
};

/** What the gateway appends to the return URL (signed with the gateway key secret). */
export interface GatewayReturn {
  razorpay_payment_id: string;
  razorpay_payment_link_id: string;
  razorpay_payment_link_reference_id?: string;
  razorpay_payment_link_status: string;
  razorpay_signature: string;
}

/** "sent", "pending" (goes out shortly) or "unavailable" (no receipt channel set up). */
export type ReceiptStatus = "sent" | "pending" | "unavailable";

export interface PaymentConfirmation {
  /** True once the payment is recorded (or the invoice was already paid). */
  confirmed: boolean;
  invoice: PublicInvoice;
  /** Number of the receipt for this payment, once it is recorded. */
  receiptNumber: string | null;
  receiptStatus: ReceiptStatus | null;
}

/** Back from the gateway: SAP verifies the return, fetches the payment itself and applies it. */
export const confirmPublicPayment = async (
  input: GatewayReturn & { token: string }
): Promise<PaymentConfirmation> => {
  const res = await apiClient.post<PaymentConfirmation>(
    "/api/billing/public/payments/confirm",
    input
  );
  return res.data;
};

export interface StatementConfirmation {
  confirmed: boolean;
  statement: PublicStatement;
  receiptNumber: string | null;
  receiptStatus: ReceiptStatus | null;
}

/** Back from a statement link: SAP verifies the return, fetches the payment and splits it. */
export const confirmStatementPayment = async (
  input: GatewayReturn & { statement: string }
): Promise<StatementConfirmation> => {
  const res = await apiClient.post<StatementConfirmation>(
    "/api/billing/public/payments/confirm",
    input
  );
  return res.data;
};

export const startPublicCheckout = async (token: string): Promise<{ url: string }> => {
  const res = await apiClient.post<{ url: string }>(
    `/api/billing/public/pay/${encodeURIComponent(token)}/checkout`
  );
  return res.data;
};
